import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Calendar, Package, Info, ShoppingCart, Eye, X, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Mock packet purchases for demo
const MOCK_PURCHASES = [
  {
    id: 'demo-packet-1',
    packet_name: 'Omaha Showcase Complete Roster',
    event_name: 'Omaha Showcase March 27-29',
    event_date: '2025-03-27',
    purchased_at: new Date().toISOString(),
    download_count: 3,
    amount_cents: 7500,
    status: 'completed'
  },
  {
    id: 'demo-packet-2',
    packet_name: 'Spring Invitational Roster',
    event_name: 'Spring Invitational Tournament',
    event_date: '2025-04-15',
    purchased_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    download_count: 1,
    amount_cents: 5000,
    status: 'completed'
  }
];

// Mock available packets for purchase
const MOCK_AVAILABLE_PACKETS = [
  {
    id: 'available-1',
    event_name: 'Summer Elite Showcase',
    event_date: '2025-06-20',
    team_count: 48,
    athlete_count: 576,
    price_cents: 7500,
    description: 'Complete roster with contact info for all 48 teams'
  },
  {
    id: 'available-2',
    event_name: 'Fall Classic Tournament',
    event_date: '2025-09-12',
    team_count: 32,
    athlete_count: 384,
    price_cents: 5000,
    description: 'Complete roster with contact info for all 32 teams'
  }
];

// Sample roster data for preview
const SAMPLE_ROSTER_DATA = [
  { name: 'Marcus Johnson', position: 'PG', gradYear: 2026, height: '6\'1"', school: 'Lincoln High', city: 'Omaha, NE', email: 'mjohnson@email.com', phone: '(402) 555-0101' },
  { name: 'Tyler Williams', position: 'SG', gradYear: 2025, height: '6\'3"', school: 'Central High', city: 'Kansas City, MO', email: 'twilliams@email.com', phone: '(816) 555-0102' },
  { name: 'Jordan Davis', position: 'SF', gradYear: 2026, height: '6\'5"', school: 'Westside Academy', city: 'Des Moines, IA', email: 'jdavis@email.com', phone: '(515) 555-0103' },
  { name: 'Chris Martinez', position: 'PF', gradYear: 2025, height: '6\'7"', school: 'North High', city: 'Lincoln, NE', email: 'cmartinez@email.com', phone: '(402) 555-0104' },
  { name: 'Brandon Lee', position: 'C', gradYear: 2026, height: '6\'9"', school: 'South High', city: 'St. Louis, MO', email: 'blee@email.com', phone: '(314) 555-0105' },
  { name: 'Darius Thompson', position: 'PG', gradYear: 2027, height: '5\'11"', school: 'East High', city: 'Minneapolis, MN', email: 'dthompson@email.com', phone: '(612) 555-0106' },
  { name: 'Andre Wilson', position: 'SG', gradYear: 2025, height: '6\'2"', school: 'West High', city: 'Chicago, IL', email: 'awilson@email.com', phone: '(312) 555-0107' },
  { name: 'Kevin Brown', position: 'SF', gradYear: 2026, height: '6\'4"', school: 'Prep Academy', city: 'Denver, CO', email: 'kbrown@email.com', phone: '(303) 555-0108' },
];

const DemoPackets: React.FC = () => {
  const [previewPacket, setPreviewPacket] = useState<typeof MOCK_AVAILABLE_PACKETS[0] | null>(null);

  const handleDownload = (packet: typeof MOCK_PURCHASES[0]) => {
    toast.success(`Demo: Downloading ${packet.packet_name}...`);
  };

  const handlePurchase = (packet: typeof MOCK_AVAILABLE_PACKETS[0]) => {
    toast.info(`Demo: In production, this would start checkout for ${packet.event_name} packet`);
  };

  const handlePreview = (packet: typeof MOCK_AVAILABLE_PACKETS[0]) => {
    setPreviewPacket(packet);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Coach Packets Demo</h1>
        <p className="text-gray-600">Preview and purchase event rosters with athlete contact information</p>
      </div>

      {/* Demo Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <Info className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Demo Mode: College Coach Packets</p>
              <p className="text-sm text-blue-700">
                This shows how college coaches can purchase and download event rosters
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Purchased Packets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            My Purchased Packets
          </CardTitle>
          <CardDescription>Packets you've purchased and can download</CardDescription>
        </CardHeader>
        <CardContent>
          {MOCK_PURCHASES.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No packets purchased yet
            </p>
          ) : (
            <div className="space-y-4">
              {MOCK_PURCHASES.map((packet) => (
                <div
                  key={packet.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="font-medium">{packet.packet_name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {packet.download_count} downloads
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(packet.event_date), 'MMM d, yyyy')}
                      </span>
                      <span>
                        Purchased: {format(new Date(packet.purchased_at), 'MMM d, yyyy')}
                      </span>
                      <span className="font-medium text-green-600">
                        ${(packet.amount_cents / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button onClick={() => handleDownload(packet)} size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Packets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Available Packets
          </CardTitle>
          <CardDescription>Purchase rosters for upcoming events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {MOCK_AVAILABLE_PACKETS.map((packet) => (
              <Card key={packet.id} className="border-2">
                <CardContent className="pt-4">
                  <h3 className="font-semibold mb-2">{packet.event_name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{packet.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(packet.event_date), 'MMM d, yyyy')}
                    </span>
                    <Badge variant="outline">{packet.team_count} teams</Badge>
                    <Badge variant="outline">{packet.athlete_count} athletes</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">
                      ${(packet.price_cents / 100).toFixed(2)}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handlePreview(packet)}>
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button size="sm" onClick={() => handlePurchase(packet)}>
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        Purchase
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!previewPacket} onOpenChange={(open) => !open && setPreviewPacket(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Roster Preview: {previewPacket?.event_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <p className="text-sm text-amber-800">
                  <strong>Sample Data Preview:</strong> This shows a sample of the data format you'll receive. 
                  The full packet includes contact information for all {previewPacket?.athlete_count} athletes across {previewPacket?.team_count} teams.
                </p>
              </CardContent>
            </Card>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-4 py-2 border-b">
                <h4 className="font-semibold">Sample Team: Omaha Elite 2026</h4>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Pos</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Height</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Contact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SAMPLE_ROSTER_DATA.map((player, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{player.name}</TableCell>
                      <TableCell>{player.position}</TableCell>
                      <TableCell>{player.gradYear}</TableCell>
                      <TableCell>{player.height}</TableCell>
                      <TableCell>{player.school}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3" />
                          {player.city}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {player.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {player.phone}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Full packet includes {previewPacket?.team_count} teams with complete contact details
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPreviewPacket(null)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setPreviewPacket(null);
                  if (previewPacket) handlePurchase(previewPacket);
                }}>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Purchase for ${previewPacket ? (previewPacket.price_cents / 100).toFixed(2) : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoPackets;
